/**
 * Live length-based validation message for the shared form fields.
 *
 * Fields are SOFT-limited: the input keeps accepting characters past
 * `maxLength` (the DOM `maxLength` attribute is intentionally NOT set), and
 * this message tells the user they are OVER the cap — exactly `maxLength`
 * characters is still valid; `maxLength + 1` is not. Persistence is blocked by
 * the shared Zod `.max()` schema + the server DTO, so an over-limit value is
 * flagged here but can never be saved.
 *
 * Returns null when the value is empty (required errors come from the explicit
 * `error` prop) or within bounds. Kept in a JSX-free module so it can be unit
 * tested directly (Field.jsx cannot be imported under `node --test`).
 */
export function lengthError(value, minLength, maxLength, label) {
  const len = (value ?? '').length
  if (len === 0) return null
  // Prefix with the field's own label so the live message matches the rest of
  // the app's validation copy ("Department Name must be at most 80
  // characters."). Falls back to a neutral subject when the label isn't a plain
  // string (e.g. a React node) or is blank.
  const field =
    typeof label === 'string' && label.trim() ? label.trim() : 'This field'
  if (minLength && len < minLength) {
    return `${field} must be at least ${minLength} characters.`
  }
  if (maxLength && len > maxLength) {
    return `${field} must be at most ${maxLength} characters.`
  }
  return null
}
